import React, { useState, useEffect } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { ref, onValue } from 'firebase/database';
import { db, database } from '../firebase';
import LoadingSpinner from './LoadingSpinner';

const AdminLiveUsersPanel = () => {
  const [usersMap, setUsersMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterPlatform, setFilterPlatform] = useState('ALL'); // ALL, WEB, MOBILE
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    let unsubFirestore = null;
    let unsubRTDB = null;

    let firestoreUsers = {};
    let rtdbUsers = {};

    const mergeUsers = () => {
      const merged = { ...rtdbUsers, ...firestoreUsers };
      setUsersMap(merged);
      setLoading(false);
    };

    // 1. Escuchar presencia desde Firestore (Colección 'presence')
    if (db) {
      try {
        const presenceColRef = collection(db, 'presence');
        unsubFirestore = onSnapshot(presenceColRef, (snapshot) => {
          const fsData = {};
          snapshot.forEach((docSnap) => {
            if (docSnap.exists()) {
              fsData[docSnap.id] = docSnap.data();
            }
          });
          firestoreUsers = fsData;
          mergeUsers();
        }, (error) => {
          console.warn("Firestore presence warning:", error?.message);
          setLoading(false);
        });
      } catch (e) {
        console.warn("Error con listener de Firestore:", e);
      }
    }

    // 2. Escuchar presencia opcional en RTDB ('usersweb')
    if (database) {
      try {
        const usersWebRef = ref(database, 'usersweb');
        unsubRTDB = onValue(usersWebRef, (snapshot) => {
          if (snapshot.exists()) {
            const val = snapshot.val();
            const rtData = {};
            Object.keys(val).forEach(uid => {
              const u = val[uid];
              if (u.presence) {
                rtData[uid] = u.presence;
              } else if (u.email || u.displayName) {
                rtData[uid] = {
                  uid,
                  email: u.email,
                  displayName: u.displayName,
                  platform: 'Página Web (Desktop)',
                  online: (Date.now() - (u.updatedAt || 0)) < 300000,
                  lastSeen: u.updatedAt || Date.now()
                };
              }
            });
            rtdbUsers = rtData;
            mergeUsers();
          }
        }, (error) => {
          // Si RTDB no tiene permisos o falla, continuar con Firestore sin interrumpir
          mergeUsers();
        });
      } catch (e) {
        mergeUsers();
      }
    }

    return () => {
      if (typeof unsubFirestore === 'function') unsubFirestore();
      if (typeof unsubRTDB === 'function') unsubRTDB();
    };
  }, []);

  const now = Date.now();
  const allUsersList = Object.values(usersMap || {});

  // Considerar online si online === true Y su lastSeen fue dentro de los últimos 2.5 minutos (150,000 ms)
  const onlineUsers = allUsersList.filter(u => u && u.online && (now - (u.lastSeen || 0) < 150000));
  
  // Conteo por plataforma en vivo
  const webUsersCount = onlineUsers.filter(u => u.platform?.includes('Desktop') || u.platform?.includes('Página Web (Desktop)')).length;
  const mobileUsersCount = onlineUsers.filter(u => u.platform?.includes('Móvil') || u.platform?.includes('App') || u.platform?.includes('Nativa')).length;

  // Filtrado de lista para la tabla
  const displayedUsers = onlineUsers.filter(user => {
    // Filtro plataforma
    if (filterPlatform === 'WEB' && !(user.platform?.includes('Desktop') || user.platform?.includes('Página Web (Desktop)'))) {
      return false;
    }
    if (filterPlatform === 'MOBILE' && !(user.platform?.includes('Móvil') || user.platform?.includes('App') || user.platform?.includes('Nativa'))) {
      return false;
    }

    // Buscador
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchName = user.displayName?.toLowerCase().includes(q);
      const matchEmail = user.email?.toLowerCase().includes(q);
      const matchRoute = user.currentRoute?.toLowerCase().includes(q);
      return matchName || matchEmail || matchRoute;
    }

    return true;
  }).sort((a, b) => (b.lastSeen || 0) - (a.lastSeen || 0));

  // Formatear tiempo
  const formatTime = (ts) => {
    if (!ts) return 'N/A';
    return new Date(ts).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const getTimeAgo = (ts) => {
    if (!ts) return 'Desconocido';
    const diffSec = Math.floor((now - ts) / 1000);
    if (diffSec < 10) return 'Hace un momento';
    if (diffSec < 60) return `Hace ${diffSec}s`;
    const diffMin = Math.floor(diffSec / 60);
    return `Hace ${diffMin}m`;
  };

  // Mapear rutas descriptivas
  const getRouteLabel = (route) => {
    if (!route) return '🏠 Inicio';
    if (route.includes('modo-guardia')) return '🛡️ Modo Guardia';
    if (route.includes('flashcards')) return '📇 Flashcards';
    if (route.includes('mapa-debilidades')) return '📊 Mapa Debilidades';
    if (route.includes('simulador')) return '📝 Simulador Examen';
    if (route.includes('quiz')) return '📋 Cuestionario';
    if (route.includes('ia-vortex')) return '🛰️ AI Vortex';
    if (route.includes('voz-vortex')) return '🎙️ Voz Vortex';
    if (route.includes('audios')) return '🎧 Biblioteca Audios';
    if (route.includes('admin')) return '⚡ Panel Administrador';
    return `📄 ${route}`;
  };

  if (loading) return <LoadingSpinner message="Sincronizando Monitor de Usuarios en Vivo..." />;

  return (
    <div style={{
      padding: '1.5rem',
      color: 'var(--text-primary)',
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem'
    }}>
      {/* Encabezado Principal */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        borderBottom: '1px solid rgba(197, 160, 89, 0.2)',
        paddingBottom: '1rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <h1 style={{
            color: '#c5a059',
            fontFamily: "'Orbitron', sans-serif",
            fontSize: '1.3rem',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            🛰️ Monitor de Usuarios en Vivo (Web y App Vortex)
          </h1>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(16, 185, 129, 0.15)',
            border: '1px solid #10b981',
            padding: '6px 14px',
            borderRadius: '20px',
            fontSize: '0.8rem',
            color: '#34d399',
            fontFamily: "'Orbitron', sans-serif",
            fontWeight: 'bold'
          }}>
            <span style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              background: '#10b981',
              boxShadow: '0 0 10px #10b981',
              display: 'inline-block',
              animation: 'pulse 1.5s infinite'
            }} />
            <span>{onlineUsers.length} USUARIOS CONECTADOS AHORA</span>
          </div>
        </div>

        <p style={{ margin: 0, fontSize: '0.85rem', color: 'rgba(181, 200, 216, 0.8)' }}>
          Supervisión en tiempo real de aspirantes activos en la página web o la app móvil Vortex.
        </p>
      </div>

      {/* Métricas de Plataforma */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '14px'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #022c22 0%, #011913 100%)',
          border: '1px solid #10b981',
          borderRadius: '12px',
          padding: '18px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          boxShadow: '0 4px 16px rgba(16, 185, 129, 0.15)'
        }}>
          <span style={{ fontSize: '2.5rem' }}>🟢</span>
          <div>
            <div style={{ fontSize: '0.7rem', color: '#a7f3d0', textTransform: 'uppercase', fontFamily: "'Orbitron', sans-serif" }}>
              Total Conectados
            </div>
            <div style={{ fontSize: '1.8rem', color: '#34d399', fontWeight: 'bold' }}>
              {onlineUsers.length}
            </div>
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #0d2035 0%, #071526 100%)',
          border: '1px solid #3b82f6',
          borderRadius: '12px',
          padding: '18px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          boxShadow: '0 4px 16px rgba(59, 130, 246, 0.15)'
        }}>
          <span style={{ fontSize: '2.5rem' }}>💻</span>
          <div>
            <div style={{ fontSize: '0.7rem', color: '#93c5fd', textTransform: 'uppercase', fontFamily: "'Orbitron', sans-serif" }}>
              Página Web (Desktop)
            </div>
            <div style={{ fontSize: '1.8rem', color: '#60a5fa', fontWeight: 'bold' }}>
              {webUsersCount}
            </div>
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #2e1065 0%, #170736 100%)',
          border: '1px solid #c5a059',
          borderRadius: '12px',
          padding: '18px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          boxShadow: '0 4px 16px rgba(197, 160, 89, 0.15)'
        }}>
          <span style={{ fontSize: '2.5rem' }}>📱</span>
          <div>
            <div style={{ fontSize: '0.7rem', color: '#e9d5ff', textTransform: 'uppercase', fontFamily: "'Orbitron', sans-serif" }}>
              App Móvil Vortex
            </div>
            <div style={{ fontSize: '1.8rem', color: '#c084fc', fontWeight: 'bold' }}>
              {mobileUsersCount}
            </div>
          </div>
        </div>
      </div>

      {/* Barra de Filtro y Buscador */}
      <div style={{
        display: 'flex',
        justify: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
        background: 'rgba(10, 26, 42, 0.8)',
        padding: '12px 16px',
        borderRadius: '10px',
        border: '1px solid rgba(197, 160, 89, 0.2)'
      }}>
        {/* Selector de Plataforma */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setFilterPlatform('ALL')}
            style={{
              background: filterPlatform === 'ALL' ? '#c5a059' : 'rgba(255,255,255,0.05)',
              color: filterPlatform === 'ALL' ? '#071526' : '#fff',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 14px',
              fontSize: '0.75rem',
              fontWeight: 'bold',
              fontFamily: "'Orbitron', sans-serif",
              cursor: 'pointer'
            }}
          >
            Todos ({onlineUsers.length})
          </button>

          <button
            onClick={() => setFilterPlatform('WEB')}
            style={{
              background: filterPlatform === 'WEB' ? '#3b82f6' : 'rgba(59, 130, 246, 0.12)',
              color: filterPlatform === 'WEB' ? '#fff' : '#60a5fa',
              border: '1px solid #3b82f6',
              borderRadius: '6px',
              padding: '8px 14px',
              fontSize: '0.75rem',
              fontWeight: 'bold',
              fontFamily: "'Orbitron', sans-serif",
              cursor: 'pointer'
            }}
          >
            💻 Web ({webUsersCount})
          </button>

          <button
            onClick={() => setFilterPlatform('MOBILE')}
            style={{
              background: filterPlatform === 'MOBILE' ? '#c5a059' : 'rgba(197, 160, 89, 0.12)',
              color: filterPlatform === 'MOBILE' ? '#fff' : '#c084fc',
              border: '1px solid #c5a059',
              borderRadius: '6px',
              padding: '8px 14px',
              fontSize: '0.75rem',
              fontWeight: 'bold',
              fontFamily: "'Orbitron', sans-serif",
              cursor: 'pointer'
            }}
          >
            📱 App Mobile ({mobileUsersCount})
          </button>
        </div>

        {/* Buscador */}
        <div style={{ flex: 1, minWidth: '220px', maxWidth: '350px' }}>
          <input
            type="text"
            placeholder="🔍 Buscar por nombre, email o sección..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              background: '#071526',
              color: '#fff',
              border: '1px solid rgba(197, 160, 89, 0.4)',
              borderRadius: '6px',
              padding: '8px 12px',
              fontSize: '0.8rem',
              outline: 'none'
            }}
          />
        </div>
      </div>

      {/* Tabla de Usuarios Activos en Vivo */}
      <div style={{
        background: 'linear-gradient(145deg, #0d2035 0%, #071526 100%)',
        border: '1px solid rgba(197, 160, 89, 0.3)',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(0,0,0,0.5)'
      }}>
        {displayedUsers.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{
                  background: 'rgba(197, 160, 89, 0.12)',
                  borderBottom: '1px solid rgba(197, 160, 89, 0.3)',
                  color: '#c5a059',
                  fontFamily: "'Orbitron', sans-serif",
                  fontSize: '0.72rem',
                  textTransform: 'uppercase'
                }}>
                  <th style={{ padding: '14px 16px' }}>Estado</th>
                  <th style={{ padding: '14px 16px' }}>Usuario</th>
                  <th style={{ padding: '14px 16px' }}>Plataforma</th>
                  <th style={{ padding: '14px 16px' }}>Sección Actual</th>
                  <th style={{ padding: '14px 16px' }}>Última Actividad</th>
                  <th style={{ padding: '14px 16px' }}>Inicio Sesión</th>
                </tr>
              </thead>
              <tbody>
                {displayedUsers.map((u, idx) => (
                  <tr key={u.uid || idx} style={{
                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    transition: 'background 0.2s',
                    background: idx % 2 === 0 ? 'rgba(0,0,0,0.1)' : 'transparent'
                  }}>
                    {/* Estado */}
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        background: 'rgba(16, 185, 129, 0.15)',
                        border: '1px solid #10b981',
                        color: '#34d399',
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                        fontFamily: "'Orbitron', sans-serif"
                      }}>
                        🟢 En Vivo
                      </span>
                    </td>

                    {/* Usuario */}
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ fontWeight: 'bold', color: '#ffffff' }}>{u.displayName || 'Aspirante'}</div>
                      <div style={{ fontSize: '0.75rem', color: 'rgba(181, 200, 216, 0.6)' }}>{u.email}</div>
                    </td>

                    {/* Plataforma */}
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        background: u.platform?.includes('Desktop') ? 'rgba(59, 130, 246, 0.15)' : 'rgba(197, 160, 89, 0.15)',
                        color: u.platform?.includes('Desktop') ? '#60a5fa' : '#c084fc',
                        border: `1px solid ${u.platform?.includes('Desktop') ? '#3b82f6' : '#c5a059'}`
                      }}>
                        {u.platform || 'Página Web'}
                      </span>
                    </td>

                    {/* Sección Actual */}
                    <td style={{ padding: '14px 16px', color: '#c5a059', fontWeight: '500' }}>
                      {getRouteLabel(u.currentRoute)}
                    </td>

                    {/* Última Actividad */}
                    <td style={{ padding: '14px 16px', color: 'rgba(181, 200, 216, 0.9)' }}>
                      <div>{getTimeAgo(u.lastSeen)}</div>
                      <div style={{ fontSize: '0.7rem', color: 'rgba(181, 200, 216, 0.5)' }}>{formatTime(u.lastSeen)}</div>
                    </td>

                    {/* Hora de Conexión */}
                    <td style={{ padding: '14px 16px', color: 'rgba(181, 200, 216, 0.7)', fontSize: '0.78rem' }}>
                      {formatTime(u.connectedAt || u.lastSeen)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: 'rgba(181, 200, 216, 0.7)'
          }}>
            <span style={{ fontSize: '2rem', display: 'block', marginBottom: '10px' }}>📡</span>
            <p style={{ margin: 0, fontWeight: 'bold' }}>No se encontraron usuarios en vivo con los filtros seleccionados.</p>
            <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem' }}>
              Cuando los usuarios ingresen a la página web o la app móvil, aparecerán aquí automáticamente en tiempo real.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLiveUsersPanel;
